import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setUseBillingAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseBillingAddress";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import ShipToSelector, { ShipToSelectorStyles } from "@insite/content-library/Components/ShipToSelector";
import CreateNewAddressModal, {
    CreateNewAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/CreateNewAddressModal";
import EditExistingAddressModal, {
    EditExistingAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/EditExistingAddressModal";
import ShippingAddressForm from "@insite/content-library/Widgets/CheckoutShipping/ShippingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Info from "@insite/mobius/Icons/Info";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField from "@insite/mobius/TextField/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { formatPhoneNumber } from "@tKgpco/Common/Utilities/formatPhoneNumber";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import SetDeliveryInfoValues from "@tKgpco/Store/Data/CheckoutShipping/Handlers/SetDeliveryInfoHandler";
import setUseOneTimeAddress from "@tKgpco/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, PropsWithChildren, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    address?: ShipToModel;
    currentBillToId: string | undefined;
    newAddress?: ShipToModel;
    oneTimeAddress?: ShipToModel;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    onChange: (address: ShipToModel, isEdit: boolean) => void;
    showUseBillingAddress?: boolean;
    isUseBillingAddressDisabled?: boolean;
    onClickUseBillingAddress?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    extendedStyles?: ShippingAddressStyles;
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
        allowCreateNewShipToAddress: getSettingsCollection(state).customerSettings.allowCreateNewShipToAddress,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        isShippingAddressUpdateRequired: state.pages.checkoutShipping.isShippingAddressUpdateRequired,
        cart: getCurrentCartState(state).value,
        isRequistionCartPresent: state.KGPState.RequistionCartStore.isRequistionCartPresent,
    };
};

const mapDispatchToProps = {
    setUseBillingAddress,
    setUseOneTimeAddress,
    setAddressBookOverlayIsOpen,
    SetDeliveryInfoValues,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface ShippingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    createNewAddressModal?: CreateNewAddressModalStyles;
    editAddressModal?: EditExistingAddressModalStyles;
    oneTimeAddressGridItem?: GridItemProps;
    oneTimeAddressCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    oneTimeAddressCheckbox?: CheckboxPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressDisplay?: AddressInfoDisplayStyles;
    useBillingAddressButton?: ButtonPresentationProps;
    title?: InjectableCss;
    headerGridItem?: GridItemProps;
    addressRadioGroup?: RadioGroupProps;
    useBillingAddressRadio?: RadioProps;
    shipToOneTimeAddress?: RadioProps;
    shipTooltip?: TooltipPresentationProps;
    asterisk?: TypographyPresentationProps;
    requiredMsgWrapper?: InjectableCss;
    deliveryinfoContainer?: GridContainerProps;
    worInfoContainer?: InjectableCss;
    worInfoNote?: TypographyPresentationProps;
}

export const shippingAddressStyles: ShippingAddressStyles = {
    requiredMsgWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            padding: 20px 0;
            width: 100%;
            font-size: 13px;
        `,
    },
    asterisk: {
        css: css`
            color: #ff0000;
            margin-right: 4px;
        `,
    },
    deliveryinfoContainer: {
        css: css`
            margin-top: 15px;
        `,
    },
    worInfoContainer: {
        css: css`
            width: 100%;
            margin-bottom: 20px;
            p {
                font-size: 13px;
                text-transform: uppercase;
                font-weight: 500;
            }
        `,
    },
    worInfoNote: {
        size: 13,
        weight: 500,
        css: css`
            display: block;
            margin-top: 20px;
        `,
    },
    container: {
        gap: 20,
        css: css`
            & > ${GridOffset} {
                margin: -15px -10px;
            }
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            padding: 15px 0;
            width: 100%;
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
            font-size: 14px !important;
        `,
    },
    selectSavedAddressLink: {
        css: css`
            text-transform: capitalize;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            padding: 0px 21px;
            min-height: 40px;
            width: 100%;
            justify-content: center;
            font-size: 14px;
            font-weight: 600;
            color: ${COLORS.secondaryBtn};
            &:hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
                span {
                    color: ${COLORS.secondaryBtn} !important;
                }
            }
        `,
    },
    addressBookModal: { sizeVariant: "medium" },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    addressBookModalCancelButton: {
        typographyProps: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            text-transform: capitalize;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            min-width: 120px;
            background: ${COLORS.white};
            color: ${COLORS.secondaryBtn};
            &:hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
            }
            &:disabled:hover {
                background: ${COLORS.disabled.base};
                border: 1px solid ${COLORS.disabled.base};
            }
        `,
    },
    oneTimeAddressGridItem: {
        width: 12,
        css: css`
            padding-left: 0;
        `,
    },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    useBillingAddressButton: {
        variant: "secondary",
        css: css`
            margin-top: 20px;
        `,
    },
    title: {
        css: css`
            font-size: 14px;
            display: flex;
            justify-content: space-between;
            width: 100%;
            align-items: center;
        `,
    },
    headerGridItem: {
        width: 12,
        css: css`
            background: #f1f6fb;
            padding: 14px;
            border-bottom: 2px solid #1e75ba;
            border-radius: 8px 8px 0 0;
            width: 100%;
        `,
    },
    addressRadioGroup: {
        css: css`
            flex-direction: row;
            width: 100%;
            justify-content: flex-start;
            @media (max-width: 1199px) and (min-width: 992px) {
                flex-direction: column;
            }
        `,
    },
    useBillingAddressRadio: {
        css: css`
            display: flex;
            align-items: baseline;
            margin-top: 0px !important;
            margin-right: 15px;
            & input + label::before {
                margin-right: 5px;
            }
            & input + label {
                font-size: 13px;
                font-weight: 500;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-left: -20px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    shipToOneTimeAddress: {
        css: css`
            display: flex;
            align-items: baseline;
            margin-top: 0px !important;
            margin-right: 5px;
            & input + label::before {
                margin-right: 5px;
            }
            & input + label {
                font-size: 13px;
                font-weight: 500;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-left: -20px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    shipTooltip: {
        iconProps: {
            src: Info,
            css: css`
                svg {
                    color: ${COLORS.primary.base};
                    width: 18px;
                    height: 18px;
                }
            `,
        },
        cssOverrides: {
            tooltipContainer: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                left: auto !important;
                                right: -5px !important;
                                &:after {
                                    content: "";
                                    left: auto !important;
                                    right: 11px !important;
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
    },
};

const ShippingAddress: FC<PropsWithChildren<Props>> = ({
    address,
    newAddress,
    oneTimeAddress,
    onChange,
    currentUserIsGuest,
    useBillingAddress,
    useOneTimeAddress,
    allowCreateNewShipToAddress,
    setUseBillingAddress,
    setUseOneTimeAddress,
    extendedStyles,
    currentBillToId,
    isUseBillingAddressDisabled,
    shippingAddressFormState,
    showUseBillingAddress,
    isShippingAddressUpdateRequired,
    countries,
    addressFieldDisplayCollection,
    theme,
    setAddressBookOverlayIsOpen,
    SetDeliveryInfoValues,
    cart,
    isRequistionCartPresent,
}) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isCreateNewAddressModalOpen, setIsCreateNewAddressModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<ShipToModel | undefined>(undefined);
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(shippingAddressStyles, extendedStyles));
    const [DeliveryInfo, setDeliveryInfoData] = useState<any>();

    const isShippingInfoPresentFromRequisition =
        cart &&
        (cart.requiresApproval || cart.isAwaitingApproval) &&
        cart.properties &&
        cart?.properties?.deliveryContactFirstName &&
        cart?.properties?.deliveryContactLastName &&
        cart?.properties?.deliveryContactPhone;

    const forbiddenChars = /[^a-zA-Z0-9\s]/;
    // Check for overly long spacing (more than one consecutive space)
    const excessiveSpacing = / {2,}/;

    useEffect(() => {
        if (isShippingInfoPresentFromRequisition) {
            setDeliveryInfoData({
                firstName: cart?.properties?.deliveryContactFirstName || "",
                lastName: cart?.properties?.deliveryContactLastName || "",
                phone: cart?.properties?.deliveryContactPhone || "",
                email: cart?.properties?.deliveryContactEmail || "",
            });
        } else if (localStorage.getItem("checkoutDeliveryInfo")) {
            const storedDeliveryInfo = localStorage.getItem("checkoutDeliveryInfo") || "{}";
            if (storedDeliveryInfo && storedDeliveryInfo !== "undefined" && storedDeliveryInfo !== "{}") {
                setDeliveryInfoData(JSON.parse(storedDeliveryInfo));
            }
        } else {
            // setDeliveryInfoData({});
        }
    }, [isShippingInfoPresentFromRequisition]);

    useEffect(() => {
        if (addressBeingEdited) {
            setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);

    useEffect(() => {
        localStorage.setItem("checkoutDeliveryInfo", JSON.stringify(DeliveryInfo));
        SetDeliveryInfoValues({
            DeliveryInfo,
        });
    }, [DeliveryInfo]);

    const oneTimeAddressChangeHandler: CheckboxProps["onChange"] = (_, value) =>
        setUseOneTimeAddress({
            useOneTimeAddress: value,
        });

    const useBillingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) =>
        setUseBillingAddress({
            useBillingAddress: value,
        });

    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = event => {
        if (event.target.value === "onetime") {
            setUseOneTimeAddress({
                useOneTimeAddress: true,
            });
        } else if (event.target.value === "billing") {
            setUseBillingAddress({
                useBillingAddress: true,
            });
        }
    };

    const selectAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsCustomerSelectorModalOpen(true);
    };

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);

    const selectCustomerHandler = (shipTo: ShipToModel) => {
        customerSelectorModalCloseHandler();
        onChange(shipTo, false);
    };

    const createNewAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsCreateNewAddressModalOpen(true);
    };

    const createNewAddressModalCloseHandler = () => setIsCreateNewAddressModalOpen(false);

    const newShippingAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address, true);
        createNewAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address has been created."),
        });
    };

    const editCustomerHandler = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        customer: BaseAddressModel,
    ) => {
        event.preventDefault();
        setAddressBeingEdited(customer as ShipToModel);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address, true);
        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address updated."),
        });
    };

    const preventDefaultHandler = (e: any) => {
        e.preventDefault();
    };

    const setDeliveryInfo = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
        const value =
            type === "phone" && e.target.value.length === 10
                ? formatPhoneNumber(e.target.value)
                : e.target.value.split("-").join("");
        // handleInputChange(e);
        setDeliveryInfoData((prev: any) => {
            return {
                ...prev,
                [type]: value,
            };
        });
    };

    return (
        <GridContainer {...styles.container} data-test-selector="checkoutShipping_shippingAddress">
            <GridItem {...styles.headerGridItem}>
                <StyledWrapper {...styles.title}>
                    <Typography {...styles.headingText} as="h3">
                        {translate("Shipping Address")}
                    </Typography>
                    {/* Adapty Custom Code */}
                    <Tooltip
                        {...styles.shipTooltip}
                        onOpen={e => preventDefaultHandler(e)}
                        onClose={e => preventDefaultHandler(e)}
                        text="Shipments outside of the United States must be handled by your KGP Logistics representative. Please note: Special shipping requirements may apply to your order"
                    />
                    {/* Adapty Custom Code */}
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.headingGridItem}>
                {(!currentUserIsGuest ||
                    (currentUserIsGuest && (allowCreateNewShipToAddress || !!newAddress || !!address))) &&
                    !isRequistionCartPresent && (
                        <Link
                            {...styles.selectSavedAddressLink}
                            type="button"
                            onClick={selectAddressClickHandler}
                            data-test-selector="checkoutShipping_selectSavedAddress"
                        >
                            {translate("Select Saved Address")}
                            <VisuallyHidden>{translate("For Shipping Address")}</VisuallyHidden>
                        </Link>
                    )}
                <Modal
                    {...styles.addressBookModal}
                    headline={translate("Address Book")}
                    isOpen={isCustomerSelectorModalOpen}
                    handleClose={customerSelectorModalCloseHandler}
                    data-test-selector="checkoutShipping_addressModal"
                >
                    <ShipToSelector
                        currentShipTo={useOneTimeAddress && shippingAddressFormState ? undefined : address}
                        currentBillToId={currentBillToId}
                        newAddress={newAddress}
                        onSelect={selectCustomerHandler}
                        onEdit={editCustomerHandler}
                        onCreateNewAddressClick={newAddress && createNewAddressHandler}
                        extendedStyles={styles.shipToSelector}
                    />
                    <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                        <Button
                            {...styles.addressBookModalCancelButton}
                            onClick={customerSelectorModalCloseHandler}
                            data-test-selector="addressModal_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </Modal>
                {newAddress && (
                    <CreateNewAddressModal
                        newAddress={newAddress}
                        isModalOpen={isCreateNewAddressModalOpen}
                        onModalClose={createNewAddressModalCloseHandler}
                        onFormSubmit={newShippingAddressFormSubmitHandler}
                    />
                )}
                {addressBeingEdited && (
                    <EditExistingAddressModal
                        newAddress={addressBeingEdited}
                        isModalOpen={isEditAddressModalOpen}
                        onModalClose={editAddressModalCloseHandler}
                        onFormSubmit={editAddressFormSubmitHandler}
                    />
                )}
            </GridItem>
            {oneTimeAddress && showUseBillingAddress ? (
                <GridItem {...styles.oneTimeAddressGridItem}>
                    <RadioGroup
                        value={
                            shippingAddressFormState
                                ? shippingAddressFormState.address.oneTimeAddress
                                    ? "onetime"
                                    : shippingAddressFormState.address.id === currentBillToId
                                      ? "billing"
                                      : ""
                                : ""
                        }
                        onChangeHandler={addressTypeChangeHandler}
                        {...styles.addressRadioGroup}
                    >
                        <Radio value="billing" {...styles.useBillingAddressRadio}>
                            {translate("Use Billing Address")}
                        </Radio>
                        <Radio
                            value="onetime"
                            data-test-selector="checkoutShipping_useOneTimeAddress"
                            {...styles.shipToOneTimeAddress}
                        >
                            {translate("Ship to One-Time Address")}
                        </Radio>
                    </RadioGroup>
                </GridItem>
            ) : (
                <>
                    {oneTimeAddress && !isRequistionCartPresent && (
                        <GridItem {...styles.oneTimeAddressGridItem}>
                            <CheckboxGroup {...styles.oneTimeAddressCheckboxGroup}>
                                <Checkbox
                                    {...styles.oneTimeAddressCheckbox}
                                    onChange={oneTimeAddressChangeHandler}
                                    checked={useOneTimeAddress}
                                    disabled={currentUserIsGuest}
                                >
                                    {translate("Ship to One-Time Address")}
                                </Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                    )}
                    {showUseBillingAddress && (
                        <GridItem {...styles.oneTimeAddressGridItem}>
                            <CheckboxGroup {...styles.oneTimeAddressCheckboxGroup}>
                                <Checkbox
                                    {...styles.oneTimeAddressCheckbox}
                                    onChange={useBillingAddressChangeHandler}
                                    checked={useBillingAddress || isUseBillingAddressDisabled}
                                    disabled={isUseBillingAddressDisabled}
                                >
                                    {translate("Use Billing Address")}
                                </Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                    )}
                </>
            )}
            <GridItem {...styles.addressDisplayAndFormGridItem}>
                {(useOneTimeAddress || currentUserIsGuest || isShippingAddressUpdateRequired) && (
                    <ShippingAddressForm countries={countries} fieldDisplay={addressFieldDisplayCollection} />
                )}
                {!useOneTimeAddress && !currentUserIsGuest && !isShippingAddressUpdateRequired && address && (
                    <AddressInfoDisplay
                        {...address}
                        state={address.state ? address.state.abbreviation : undefined}
                        country={address.country ? address.country.abbreviation : undefined}
                        extendedStyles={styles.addressDisplay}
                    />
                )}
                {!useOneTimeAddress ? (
                    <>
                        <GridItem width={12}>
                            <StyledWrapper {...styles.worInfoContainer}>
                                <Typography as="p">{`${DeliveryInfo?.firstName || ""} ${
                                    DeliveryInfo?.lastName || ""
                                }`}</Typography>
                                <Typography as="p">{`${DeliveryInfo?.phone || ""}`}</Typography>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.headerGridItem}>
                            <StyledWrapper {...styles.title}>
                                <Typography as="h3" {...styles.headingText}>
                                    {translate("Delivery Contact")}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>

                        <GridContainer {...styles.deliveryinfoContainer}>
                            <GridItem width={6}>
                                <TextField
                                    name="firstName"
                                    value={DeliveryInfo?.firstName}
                                    required
                                    label={translate("First Name")}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setDeliveryInfo(e, "firstName")
                                    }
                                    disabled={Boolean(cart && cart?.properties?.deliveryContactFirstName)}
                                    // error={formErrors.firstName}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    name="lastName"
                                    value={DeliveryInfo?.lastName}
                                    required
                                    label={translate("Last Name")}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setDeliveryInfo(e, "lastName")
                                    }
                                    disabled={Boolean(cart && cart?.properties?.deliveryContactLastName)}
                                    // error={formErrors.lastName}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    name="phone"
                                    value={DeliveryInfo?.phone}
                                    required
                                    label={translate("Phone")}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeliveryInfo(e, "phone")}
                                    disabled={Boolean(cart && cart?.properties?.deliveryContactPhone)}
                                    // error={formErrors.phone}
                                />
                            </GridItem>
                            <GridItem
                                width={12}
                                css={css`
                                    flex-direction: column;
                                `}
                            >
                                <TextField
                                    name="email"
                                    value={DeliveryInfo?.email}
                                    label={translate("Email")}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeliveryInfo(e, "email")}
                                    // error={formErrors.email}
                                    disabled={Boolean(
                                        (cart && cart?.properties?.deliveryContactEmail) || isRequistionCartPresent,
                                    )}
                                />
                                <StyledWrapper {...styles.requiredMsgWrapper}>
                                    <Typography {...styles.asterisk}>*</Typography>
                                    {translate("Required")}
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </>
                ) : null}
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(ShippingAddress));
